import React, { useEffect, useState, useCallback } from 'react';
import { Spin, Typography, Empty, Button, message, Modal } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
} from '@ant-design/icons';
import AppLayout from '../../components/Layout/AppLayout';
import { useAuthToken } from '../../hooks/useAuthToken';
import { useVendor } from '../../context/VendorContext';
import { api } from '../../services/api';
import { BuyerProfileModal } from '../../components/Modals';
import AvatarImage from '../../components/AvatarImage';
import { getAvatarUrl, s3KeyToSeed } from '../../utils/avatars';

const { Title, Text } = Typography;

const PERSONALITY_LABELS = {
  SKEPTICAL_AND_BUSY: 'Skeptical & Busy',
  ANALYTICAL_AND_DETAIL_ORIENTED: 'Analytical & Detail-Oriented',
  FRIENDLY_AND_OPEN: 'Friendly & Open',
  AGGRESSIVE_AND_COST_FOCUSED: 'Aggressive & Cost-Focused',
  INDECISIVE_AND_RISK_AVERSE: 'Indecisive & Risk-Averse',
};

const VOICE_PROVIDER_LABELS = {
  eleven_labs: 'ElevenLabs',
  deepgram: 'Deepgram',
  cartesia: 'Cartesia',
  aws_polly: 'AWS Polly',
};

const BuyerProfilesPage = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);

  useAuthToken();
  const { selectedVendorId, isPublic } = useVendor();

  const loadAgents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getRoleplayAgents(
        selectedVendorId != null ? { vendorId: selectedVendorId } : {}
      );
      const envelope = res.data?.data;
      const rows = envelope?.data ?? [];

      setAgents(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Could not load buyer profiles.');
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, [selectedVendorId]);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  const handleAdd = () => {
    setEditingAgent(null);
    setModalOpen(true);
  };

  const handleEdit = async (e, agent) => {
    e.stopPropagation();
    try {
      const res = await api.getRoleplayAgentById(agent.id);
      const fullAgent = res?.data?.data ?? agent;
      setEditingAgent(fullAgent);
      setModalOpen(true);
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to load buyer profile details');
    }
  };

  const handleDelete = (e, id) => {
    e.stopPropagation();
    Modal.confirm({
      title: 'Delete this buyer profile?',
      content: 'This cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await api.deleteRoleplayAgent(id);
          message.success('Buyer profile deleted');
          loadAgents();
        } catch (err) {
          message.error(err?.response?.data?.message || 'Failed to delete buyer profile');
        }
      },
    });
  };

  const handleSaveSuccess = useCallback(() => {
    loadAgents();
  }, [loadAgents]);

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingAgent(null);
  };

  return (
    <AppLayout>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div
          style={{
            marginBottom: 28,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
          }}
        >
          <div>
            <Title level={2} style={{ marginBottom: 8 }}>
              Buyer Profiles
            </Title>
            <Text type="secondary">
              Manage buyer personas used in roleplay scenarios
              {isPublic ? ' (public catalog)' : ' for the selected vendor'}.{' '}
              {agents.length > 0 && (
                <span>
                  {agents.length} {agents.length === 1 ? 'profile' : 'profiles'}
                </span>
              )}
            </Text>
          </div>
          <Button type="primary" size="large" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Buyer Profile
          </Button>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '64px 0' }}>
            <Spin size="large" />
          </div>
        )}

        {!loading && error && (
          <div
            className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 text-sm mb-6"
            role="alert"
          >
            {error}
            <Button type="link" size="small" onClick={loadAgents} style={{ paddingLeft: 8 }}>
              Retry
            </Button>
          </div>
        )}

        {!loading && !error && agents.length === 0 && (
          <Empty
            description="No buyer profiles yet"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: '48px 0' }}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Add your first buyer profile
            </Button>
          </Empty>
        )}

        {!loading && agents.length > 0 && (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            style={{ marginTop: 8 }}
          >
            {agents.map((agent) => (
              <div
                key={agent.id}
                className="rounded-2xl border border-slate-200/90 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)] p-5 flex flex-col transition-all hover:border-[#0842A0]/40 hover:shadow-md"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center shrink-0 text-lg overflow-hidden">
                    {agent.avatarS3Key ? (
                      <AvatarImage
                        src={agent.avatarImageUrl || getAvatarUrl(s3KeyToSeed(agent.avatarS3Key))}
                        fallbackSrc={getAvatarUrl(s3KeyToSeed(agent.avatarS3Key))}
                        size={48}
                        className="rounded-full"
                      />
                    ) : (
                      <UserOutlined />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 line-clamp-1">
                      {agent.name}
                    </div>
                    {(agent.jobTitle || agent.companyName) && (
                      <div className="text-sm text-slate-500 mt-0.5">
                        {[agent.jobTitle, agent.companyName].filter(Boolean).join(' at ')}
                      </div>
                    )}
                    <div className="text-xs text-slate-400 mt-1 flex flex-wrap gap-x-2">
                      {agent.gender && (
                        <span>{agent.gender === 'MALE' ? 'Male' : 'Female'}</span>
                      )}
                      {agent.industry && (
                        <span>· {agent.industry.replace(/_/g, ' ')}</span>
                      )}
                      {agent.voiceProvider && (
                        <span>· {VOICE_PROVIDER_LABELS[agent.voiceProvider] || agent.voiceProvider}{agent.voiceId ? ` (${agent.voiceId})` : ''}</span>
                      )}
                    </div>
                    {agent.personalityPreset && (
                      <div className="text-xs text-slate-600 mt-2 flex items-center gap-1.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-100">
                          {PERSONALITY_LABELS[agent.personalityPreset] || agent.personalityPreset}
                        </span>
                      </div>
                    )}
                    {agent.personalityPresetPrompt && (
                      <div className="text-xs text-slate-500 mt-1 line-clamp-2 italic" title={agent.personalityPresetPrompt}>
                        "{agent.personalityPresetPrompt}"
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-auto pt-4 flex flex-wrap gap-2 justify-end border-t border-slate-100">
                  <Button
                    type="default"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={(e) => handleEdit(e, agent)}
                  >
                    Edit
                  </Button>
                  {/* Delete is disabled for now */}
                  {/* <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={(e) => handleDelete(e, agent.id)}
                  >
                    Delete
                  </Button> */}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BuyerProfileModal
        visible={modalOpen}
        agent={editingAgent}
        onCancel={handleModalClose}
        onSuccess={handleSaveSuccess}
      />
    </AppLayout>
  );
};

export default BuyerProfilesPage;
